import { useEffect, useState } from 'react';

interface UseBreakpointArgs {
  type: 'min-width' | 'max-width';
  breakpoint: number;
}

/**
 * ### useBreakpoint
 *
 * This hook can be used to handle a media query breakpoint using JavaScript.
 *
 * @param type - the type of breakpoint. i.e. 'min-width' or 'max-width'
 * @param breakpoint - a number representing the desired breakpoint value
 * @returns boolean
 */
export const useBreakpoint = ({ type, breakpoint }: UseBreakpointArgs): boolean => {
  const [isMatch, setIsMatch] = useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const mediaWatcher = window.matchMedia(`screen and (${type}: ${breakpoint}px)`);

    setIsMatch(mediaWatcher.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMatch(e.matches);
    };

    if (mediaWatcher.addEventListener) {
      mediaWatcher.addEventListener('change', handleChange);
      return () => {
        mediaWatcher.removeEventListener('change', handleChange);
      };
    } else {
      // Keeping around these deprecated methods to support older
      // versions of Safari.
      mediaWatcher.addListener(handleChange);
      return () => {
        mediaWatcher.removeListener(handleChange);
      };
    }
  }, [breakpoint, type, setIsMatch]);

  return isMatch;
};
